// @ts-strict-ignore
import type {CurrentProject} from 'common/report/model-resources/projects';
import {ProjectDetails} from 'common/report/stores/project-details';
import {getProjectId, isCurrentProjectInternal, isUrlDefinedExperimentalFeatureEnabled} from 'common/report/util';
import {isDevEnv, isTestEnv} from 'common/report/util/env';
import {getLocation} from 'common/util/url';

import {FeatureGate, ProjectFeatureFlag} from '../constants';

export const featureSamplingUtils = {
  determineFeatureEligibility,
};

/**
 * Check deterministically whether a user or group is included in a feature rollout/test based on a
 * unique user/group string identifier and the desired percentage to include.
 */
function determineFeatureEligibility(
  userOrGroupId: string,
  feature: string,
  percentAllowed: number /* must be in range [0, 100] */,
): boolean {
  if (!(percentAllowed >= 0 && percentAllowed <= 100)) {
    throw new Error(`@percentAllowed argument must be in range [0, 100] inclusive`);
  }

  if (typeof userOrGroupId === `undefined` || userOrGroupId === null || userOrGroupId === ``) {
    throw new Error(`@userOrGroupId argument cannot be null or undefined or empty`);
  }
  if (typeof feature === `undefined` || feature === null || feature === ``) {
    throw new Error(`@feature argument cannot be null or undefined or empty`);
  }

  switch (percentAllowed) {
    case 0:
      return false;
    case 100:
      return true;
    default:
      break;
  }

  const key = userOrGroupId + feature;

  // Bernstein's hash: http://www.cse.yorku.ca/~oz/hash.html#djb2
  // The djb2 hash was chosen because of the claim that "it has excellent distribution and speed on many different sets of keys and table sizes",
  // but we have not validated whether it is statistically reliable and accurate to use in sampling users/groups for rollouts and analysis.
  let hash = 5381;
  for (let i = 0; i < key.length; i++) {
    hash = (hash << 5) + hash + key.charCodeAt(i);
    hash = hash & hash;
  }
  const dart = (hash >>> 0) % 100;
  return dart < percentAllowed;
}

enum FeatureRolloutType {
  Project,
  Org,
}
export class FeatureRollout {
  private declare type: FeatureRolloutType;
  private declare percentage: number;
  public declare readonly featureflag: ProjectFeatureFlag;
  private explicitlyDisallowIds: string[] = [];

  public declare readonly name: FeatureRolloutName;

  constructor(params: {
    type: FeatureRolloutType;
    name: FeatureRolloutName;
    percentage: number;
    featureflag?: ProjectFeatureFlag;
    explicitlyDisallowIds?: string[];
  }) {
    this.type = params.type;
    this.name = params.name;
    this.percentage = params.percentage;
    this.featureflag = params.featureflag;
    this.explicitlyDisallowIds = params.explicitlyDisallowIds ?? [];
  }

  isEnabled(project: CurrentProject | ProjectDetails | null): boolean {
    if (this.type === FeatureRolloutType.Project) {
      const projectId = project?.id.toString() ?? getProjectId();
      if (this.explicitlyDisallowIds.includes(projectId)) {
        return false;
      }

      return (
        featureSamplingUtils.determineFeatureEligibility(projectId, this.name.toString(), this.percentage) ||
        project?.hasFeatureFlag(this.featureflag)
      );
    }
    const orgId = project?.organizationId.toString();
    if (this.explicitlyDisallowIds.includes(orgId)) {
      return false;
    }

    return orgId
      ? featureSamplingUtils.determineFeatureEligibility(orgId, this.name.toString(), this.percentage) ||
          project?.hasFeatureFlag(this.featureflag)
      : false;
  }
}

// Add any gradual feature rollouts here
export enum FeatureRolloutName {
  OnboardingChecklist = `onboarding-checklist`,
  Revenue = `revenue`,
  Home = `home`,
  WarehouseLaunchTemplates = `wh_launch_templates`,
  AutomatedEventApproval = `automated_event_approval`,
  DataStandards = `data-standards`,
  DataGovernanceToLexiconTab = `data_gov_to_lexicon`,
  DisableVizOnNonCompliant = `disable_viz_non_comp`,
  CompanyProfile = `company_profile`,
  SessionRecordingServerSideStitching = `sr_server_side_stitching`,
  // Start - Lexicon new tables rollout
  LexiconNewTableEventProperties = `lexicon_table_new_properties`,
  LexiconNewLookupTable = `lexicon_table_new_lookup`,
  LexiconNewCustomEventTable = `lexicon_table_new_custom_events`,
  // End - Lexicon new tables rollout
  ShowCohortsInLexicon = `show_cohorts_in_lexicon`,
  BoardBreakdowns = `board_breakdowns`,
  OnboardingQuestionnaire = `onboarding_questionnaire`,
  OptimizedFunnelSelection = `optimized_funnels`,
  LexiconQueryApi = `lexicon_query_api`,
  LexiconColumnSelection = `lexicon_column_selection`,
  LexiconPropertiesQueryApi = `lexicon_properties_query_api`,
  LexiconColumnResizing = `lexicon_column_resizing`,
  InsightsMetricsLimits = `insights_metrics_limits`,
  SessionReplayEventDetails = `session_replay_event_details`,
}

export const FeatureRollouts: {[key in FeatureRolloutName]: FeatureRollout} = {
  [FeatureRolloutName.LexiconPropertiesQueryApi]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.LexiconPropertiesQueryApi,
    percentage: 100,
  }),
  [FeatureRolloutName.OnboardingChecklist]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.OnboardingChecklist,
    percentage: 0,
  }),
  [FeatureRolloutName.Revenue]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.Revenue,
    featureflag: ProjectFeatureFlag.Revenue,
    percentage: 0,
  }),
  [FeatureRolloutName.Home]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.Home,
    percentage: 100,
    explicitlyDisallowIds: [`1866253`],
  }),
  [FeatureRolloutName.WarehouseLaunchTemplates]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.WarehouseLaunchTemplates,
    percentage: 0,
    featureflag: ProjectFeatureFlag.WarehouseLaunchTemplates,
  }),
  [FeatureRolloutName.DataStandards]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.DataStandards,
    percentage: 100,
    featureflag: ProjectFeatureFlag.DataStandards,
  }),
  [FeatureRolloutName.AutomatedEventApproval]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.AutomatedEventApproval,
    percentage: 100,
    featureflag: ProjectFeatureFlag.AutomatedEventApproval,
  }),
  [FeatureRolloutName.DataGovernanceToLexiconTab]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.DataGovernanceToLexiconTab,
    percentage: 100,
    featureflag: ProjectFeatureFlag.DataGovernanceToLexiconTab,
  }),
  [FeatureRolloutName.DisableVizOnNonCompliant]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.DisableVizOnNonCompliant,
    percentage: 0,
    featureflag: ProjectFeatureFlag.DisableVizOnNonCompliant,
  }),
  [FeatureRolloutName.CompanyProfile]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.CompanyProfile,
    percentage: 100,
    featureflag: ProjectFeatureFlag.CompanyProfile,
  }),
  [FeatureRolloutName.SessionRecordingServerSideStitching]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.SessionRecordingServerSideStitching,
    percentage: 100,
    featureflag: ProjectFeatureFlag.SessionRecordingServerSideStitching,
  }),
  [FeatureRolloutName.LexiconNewTableEventProperties]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.LexiconNewTableEventProperties,
    percentage: 100,
    featureflag: ProjectFeatureFlag.LexiconNewTableProperties,
  }),
  [FeatureRolloutName.ShowCohortsInLexicon]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.ShowCohortsInLexicon,
    percentage: 0,
    featureflag: ProjectFeatureFlag.ShowCohortsInLexicon,
  }),
  [FeatureRolloutName.BoardBreakdowns]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.BoardBreakdowns,
    percentage: 100,
    featureflag: ProjectFeatureFlag.BoardBreakdowns,
  }),
  [FeatureRolloutName.OnboardingQuestionnaire]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.OnboardingQuestionnaire,
    percentage: 100,
    featureflag: ProjectFeatureFlag.OnboardingQuestionnaire,
  }),
  [FeatureRolloutName.OptimizedFunnelSelection]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.OptimizedFunnelSelection,
    percentage: 0,
    featureflag: ProjectFeatureFlag.OptimizedFunnelSelection,
  }),
  [FeatureRolloutName.LexiconNewLookupTable]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.LexiconNewLookupTable,
    percentage: 100,
  }),
  [FeatureRolloutName.LexiconNewCustomEventTable]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.LexiconNewCustomEventTable,
    percentage: 100,
  }),
  [FeatureRolloutName.LexiconQueryApi]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.LexiconQueryApi,
    percentage: 100,
  }),
  [FeatureRolloutName.LexiconColumnSelection]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.LexiconColumnSelection,
    percentage: 100, // TODO(akim): clean up once rolled out for long enough
    featureflag: ProjectFeatureFlag.LexiconColumnSelection,
  }),
  [FeatureRolloutName.LexiconColumnResizing]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.LexiconColumnResizing,
    percentage: 50,
    featureflag: ProjectFeatureFlag.LexiconColumnResizing,
  }),
  [FeatureRolloutName.InsightsMetricsLimits]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.InsightsMetricsLimits,
    percentage: 0,
    featureflag: ProjectFeatureFlag.InsightsMetricsLimits,
  }),
  [FeatureRolloutName.SessionReplayEventDetails]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.SessionReplayEventDetails,
    percentage: 0,
  }),
};
export const RolloutsByFeatureFlag: Partial<Record<ProjectFeatureFlag, FeatureRollout>> = Object.fromEntries(
  Object.values(FeatureRollouts).map((value) => [value.featureflag, value]),
);

export function useTemplateSearch() {
  if (isTestEnv()) {
    return true;
  }
  return isUrlDefinedExperimentalFeatureEnabled(getLocation(), `template_search`, () => isCurrentProjectInternal());
}

export function isDataVolumeMonitoringEnabled(currentProject?: CurrentProject) {
  if (!currentProject) {
    return false;
  }
  return currentProject.getFeatureGate(FeatureGate.DataGovernance);
}

export function isHeatMapsEnabled(currentProject?: CurrentProject) {
  if (!currentProject) {
    return false;
  }

  const hasSessionReplayEnabled = isSessionRecordingEnabled(currentProject);

  return hasSessionReplayEnabled && currentProject.hasFeatureFlag(ProjectFeatureFlag.HeatMaps);
}

export function getSessionRecordingBlockingInfo(currentProject?: CurrentProject) {
  if (!currentProject) {
    return [];
  }
  return currentProject.blocking.blockedReplayWindows || [];
}

export function isSessionRecordingEnabled(currentProject?: CurrentProject) {
  if (!currentProject) {
    return false;
  }

  const hasSessionReplayFeatureGate = currentProject.getFeatureGate(FeatureGate.SessionReplay);
  const hasFeatureFlagEnabled = currentProject.hasFeatureFlag(ProjectFeatureFlag.SessionRecording);
  const hasSessionReplayEnabled = hasSessionReplayFeatureGate || hasFeatureFlagEnabled;
  if (currentProject.hasFeatureFlag(ProjectFeatureFlag.SessionRecordingSensitiveData)) {
    return hasSessionReplayEnabled && currentProject.hasSensitiveAccess;
  }

  return hasSessionReplayEnabled;
}

export function isSegmentRegionEnabled() {
  return true;
}

export function isDataStandardsEnabled(currentProject?: CurrentProject | ProjectDetails) {
  if (isDevEnv()) {
    return true;
  }

  if (!currentProject) {
    return false;
  }

  return (
    currentProject.getFeatureGate(FeatureGate.DataStandards) &&
    (isUrlDefinedExperimentalFeatureEnabled(getLocation(), `data-standards`, true) ||
      FeatureRollouts[FeatureRolloutName.DataStandards].isEnabled(currentProject))
  );
}

export function isAutomateEventApprovalEnabled(currentProject?: CurrentProject | ProjectDetails) {
  if (isDevEnv()) {
    return true;
  }

  if (!currentProject) {
    return false;
  }

  return (
    currentProject.getFeatureGate(FeatureGate.DataStandards) &&
    (currentProject.hasFeatureFlag(ProjectFeatureFlag.AutomatedEventApproval) ||
      FeatureRollouts[FeatureRolloutName.AutomatedEventApproval].isEnabled(currentProject))
  );
}

export function isDisableVisibleOnNonCompliantEnabled(currentProject?: CurrentProject | ProjectDetails): boolean {
  if (isDevEnv()) {
    return true;
  }

  if (!currentProject) {
    return false;
  }

  return (
    currentProject.getFeatureGate(FeatureGate.DataStandards) && // has data standards feature gate
    isDataStandardsEnabled(currentProject) && // data standards feature is enabled
    currentProject.getFeatureGate(FeatureGate.DataGovernance) && // has event approval feature gate, this check is necessary in case account downgrade
    currentProject?.hasEventApprovalEnabled && // event approval is enabled and turned on
    FeatureRollouts[FeatureRolloutName.DisableVizOnNonCompliant].isEnabled(currentProject)
  );
}

export function isDataGovToLexiconEnabled(currentProject?: CurrentProject | ProjectDetails) {
  if (isDevEnv()) {
    return true;
  }

  if (!currentProject) {
    return false;
  }

  return (
    isUrlDefinedExperimentalFeatureEnabled(getLocation(), `data-gov-to-lex`, true) ||
    FeatureRollouts[FeatureRolloutName.DataGovernanceToLexiconTab].isEnabled(currentProject)
  );
}

export function isCompanyProfileEnabled(currentProject?: CurrentProject | ProjectDetails | null) {
  if (isDevEnv()) {
    return true;
  }
  return FeatureRollouts[FeatureRolloutName.CompanyProfile].isEnabled(currentProject);
}

/**
 * Event Props, Profile Props, Custom Event Props, Custom Profile Props
 *
 */
export function isNewPropertiesUIEnabled(currentProject?: CurrentProject) {
  if (!currentProject) {
    return false;
  }
  return (
    FeatureRollouts[FeatureRolloutName.LexiconNewTableEventProperties].isEnabled(currentProject) ||
    isCurrentProjectInternal()
  );
}

export function isLookupTablesNewTableEnabled(currentProject?: CurrentProject) {
  if (!currentProject) {
    return false;
  }

  return (
    FeatureRollouts[FeatureRolloutName.LexiconNewLookupTable].isEnabled(currentProject) ||
    isUrlDefinedExperimentalFeatureEnabled(getLocation(), `enable-new-lookup-table`, true)
  );
}

export function isCustomEventsNewTableEnabled(currentProject?: CurrentProject) {
  if (!currentProject) {
    return false;
  }
  return (
    FeatureRollouts[FeatureRolloutName.LexiconNewCustomEventTable].isEnabled(currentProject) ||
    isUrlDefinedExperimentalFeatureEnabled(getLocation(), `enable-new-custom-events-table`, true)
  );
}

export function isCohortsInLexiconEnabled(currentProject?: CurrentProject) {
  if (!currentProject) {
    return false;
  }
  return (
    FeatureRollouts[FeatureRolloutName.ShowCohortsInLexicon].isEnabled(currentProject) ||
    (isDevEnv() && isUrlDefinedExperimentalFeatureEnabled(getLocation(), `enable-cohorts`, false))
  );
}

export function isInsightsMetricsLimitsEnabled(currentProject?: CurrentProject) {
  if (!currentProject) {
    return false;
  }
  return (
    currentProject.hasFeatureFlag(ProjectFeatureFlag.InsightsMetricsLimits) ||
    FeatureRollouts[FeatureRolloutName.InsightsMetricsLimits].isEnabled(currentProject)
  );
}

export function isMetricLevelDataGroupsEnabled(currentProject?: CurrentProject | ProjectDetails | null) {
  if (!currentProject) {
    return false;
  }
  return currentProject.hasFeatureFlag(ProjectFeatureFlag.MetricLevelDataGroups);
}

export function isFirstClassMetricsEnabled(currentProject: CurrentProject | null) {
  if (!currentProject) {
    return false;
  }
  return currentProject.hasFeatureFlag(ProjectFeatureFlag.FirstClassMetrics);
}

export function isQueryApiForLexiconEventsEnabled(currentProject: CurrentProject | ProjectDetails | null) {
  return (
    FeatureRollouts[FeatureRolloutName.LexiconQueryApi].isEnabled(currentProject) ||
    isDevEnv() ||
    isUrlDefinedExperimentalFeatureEnabled(getLocation(), `use-query-api-for-lexicon`, true)
  );
}

export function isQueryApiForLexiconPropertiesEnabled(currentProject: CurrentProject | ProjectDetails | null) {
  return (
    FeatureRollouts[FeatureRolloutName.LexiconPropertiesQueryApi].isEnabled(currentProject) ||
    isDevEnv() ||
    isUrlDefinedExperimentalFeatureEnabled(getLocation(), `use-query-api-for-lexicon`, true)
  );
}

export function isLexiconColumnSelectionEnabled(currentProject: CurrentProject | null | undefined) {
  return (
    FeatureRollouts[FeatureRolloutName.LexiconColumnSelection].isEnabled(currentProject ?? null) ||
    isUrlDefinedExperimentalFeatureEnabled(getLocation(), `lexicon-column-selection`, true)
  );
}

export function isLexiconColumnResizingEnabled(currentProject: CurrentProject | null | undefined) {
  return (
    FeatureRollouts[FeatureRolloutName.LexiconColumnResizing].isEnabled(currentProject) ||
    isDevEnv() ||
    isUrlDefinedExperimentalFeatureEnabled(getLocation(), `lexicon-column-selection`, true)
  );
}

export function isSessionReplayEventDetailsEnabled(currentProject?: CurrentProject | ProjectDetails) {
  return (
    FeatureRollouts[FeatureRolloutName.SessionReplayEventDetails].isEnabled(currentProject ?? null) ||
    isUrlDefinedExperimentalFeatureEnabled(getLocation(), `session-replay-event-details`, true)
  );
}

// TODO Update function once it is confirmed how to actually get the variant for the current user
export function isOwnersImprovementEnabled() {
  const variant = getFeatureFlagVariant(ProjectFeatureFlag.OnwersImprovements);
  return isDevEnv() || variant;
}

function getFeatureFlagVariant(flagName: string, fallbackValue?: string) {
  return mixpanel.flags.get_variant_value_sync(flagName, fallbackValue);
}
